import { useEffect, useId, useRef, useState } from 'react';
import CupMark from './CupMark.jsx';
import { RailNavIcon } from './CollapsibleRail.jsx';

/**
 * Floating mobile navigation shared by the institution portal and super-admin
 * console. Its geometry mirrors the main app's MobileNav: a solid brand pill
 * stays above the page while a second rounded surface reveals the full menu.
 * `identity` (portal only) replaces the centered wordmark with the school's
 * crest, name and accent rule, and shrinks the cup to a small mark.
 */
export default function MobilePillNav({
  ariaLabel,
  groups,
  activeId,
  onSelect,
  account,
  menuHeader,
  identity,
  className = '',
  hidden = false,
  t,
}) {
  const [open, setOpen] = useState(false);
  const shellRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) { return undefined; }

    const closeOnOutsidePress = (event) => {
      if (!shellRef.current?.contains(event.target)) { setOpen(false); }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') { setOpen(false); }
    };

    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const selectItem = (id) => {
    onSelect(id);
    setOpen(false);
  };

  // Console keeps the clay label (main-client MobileNav). The portal sets the
  // label in ink with a clay inset marker, since clay text is under AA on cream.
  const clayLabel = t.name === 'console';

  return (
    <div
      ref={shellRef}
      className={`fixed inset-x-0 top-0 z-40 pointer-events-none ${className}`}
      style={{ top: 'env(safe-area-inset-top, 0px)' }}
      aria-hidden={hidden || undefined}
      inert={hidden || undefined}
    >
      <div className="relative mx-auto mt-3 w-[calc(100%-32px)] max-w-[720px] pointer-events-auto">
        <div
          data-mobile-pill-nav
          className="h-16 rounded-[32px] border px-3 flex items-center justify-between overflow-hidden"
          style={{ backgroundColor: t.surface, borderColor: t.hairline }}
        >
          {identity ? (
            <>
              <div className="min-w-0 flex-1">{identity}</div>
              <div className="ml-2 h-10 w-6 shrink-0 flex items-center justify-center">
                <CupMark size={20} />
              </div>
            </>
          ) : (
            <>
              <div className="h-10 w-10 shrink-0 flex items-center justify-center">
                <CupMark size={32} />
              </div>

              <div
                className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[18px] leading-none font-semibold"
                style={{ color: t.heading, fontFamily: t.fonts.display }}
                aria-hidden="true"
              >
                Coffee Chats<span style={{ color: t.accent }}>.</span>
              </div>
            </>
          )}

          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => { setOpen((current) => { return !current; }); }}
            className={`relative h-10 w-10 shrink-0 rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
            style={{ color: t.heading }}
          >
            <span
              className={`absolute h-[2px] w-6 rounded-full bg-current transition-transform duration-300 ${open ? 'translate-y-0 rotate-45' : '-translate-y-1'}`}
            />
            <span
              className={`absolute h-[2px] w-6 rounded-full bg-current transition-transform duration-300 ${open ? 'translate-y-0 -rotate-45' : 'translate-y-1'}`}
            />
          </button>
        </div>

        <div
          id={menuId}
          data-mobile-pill-menu
          aria-hidden={!open}
          inert={!open || undefined}
          className={`absolute inset-x-0 top-full mt-2 overflow-y-auto rounded-[28px] border px-2 py-2 origin-top transition-all duration-300 ${open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'}`}
          style={{
            backgroundColor: t.surface,
            borderColor: t.hairline,
            // Scrolls instead of running off a short phone (the console has 15 rows).
            maxHeight: 'calc(100dvh - 96px - env(safe-area-inset-top, 0px))',
          }}
        >
          {menuHeader ? (
            <div className="px-2 pb-2 mb-1 border-b" style={{ borderColor: t.hairline }}>
              {menuHeader}
            </div>
          ) : null}

          <nav aria-label={ariaLabel}>
            {(groups || []).map((group, groupIndex) => {
              return (
                <div key={group.label || groupIndex}>
                  {groupIndex > 0 ? (
                    <div className="flex items-center gap-2 px-2 py-1" aria-hidden="true">
                      <span className="h-px flex-1" style={{ backgroundColor: t.hairline }} />
                      <span
                        className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                        style={{ color: t.muted, fontFamily: t.fonts.mono }}
                      >
                        {group.label}
                      </span>
                      <span className="h-px flex-1" style={{ backgroundColor: t.hairline }} />
                    </div>
                  ) : null}

                  <div className="flex flex-col">
                    {group.items.map((item) => {
                      const active = item.id === activeId;
                      let itemColor = t.muted;
                      if (active) { itemColor = clayLabel ? t.accent : t.heading; }
                      return (
                        <button
                          key={item.id}
                          type="button"
                          aria-current={active ? 'page' : undefined}
                          aria-description={item.locked ? 'Permission required' : undefined}
                          onClick={() => { selectItem(item.id); }}
                          className={`min-h-[44px] rounded-lg px-2.5 flex items-center gap-2.5 text-left text-[18px] leading-6 font-semibold transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                          style={{
                            color: itemColor,
                            backgroundColor: 'transparent',
                            fontFamily: t.fonts.body,
                            boxShadow: active && !clayLabel ? `inset 2px 0 0 ${t.accent}` : 'none',
                          }}
                        >
                          <RailNavIcon name={item.icon} size={20} />
                          <span className="min-w-0 truncate">{item.label}</span>
                          {item.locked ? (
                            <span className="ml-auto shrink-0 text-[13px] uppercase tracking-wide" style={{ color: t.muted }}>
                              Locked
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {account ? (
            <div className="mt-1 border-t" style={{ borderColor: t.hairline }}>
              {account}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
